import { useForm } from "react-hook-form";
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { BusinessUser } from "../../../service/model/user.model";
import { Button, Grid } from "@mui/material";
import FormField from "../../../components/form-controls/form-field";
import theme from "../../../app/app-theme";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { selectLoggedInUser } from "../../auth/auth.slice";
import useRoles from "../../../hooks/use-roles";
import { getPermissionsForLoggedInUser, isAdmin } from "../../../shared/utils";
import { createUser, updateBusinessUserProfile } from "../user.slice";
import { FormTextField } from "../../../components/form-input-field-wrappers/form-controller-inputs/form-text-field";
import { FormSelectField } from "../../../components/form-input-field-wrappers/form-controller-inputs/form-select-field";
import { PermissionCode } from "../../../shared/enums";

interface BusinessUserFormProps {
    readonly user?: BusinessUser
    readonly editable?: boolean
    readonly onClose: () => void
    // readonly onSubmit: (data: User) => void
}

const validationSchema = yup.object().shape({
    username: yup.string().required("Username is required"),
    firstName: yup.string().required("First name is required"),
    lastName: yup.string().required("Last name is required"),
    roleId: yup.number().required("Role is required"),
    email: yup.string().email("Not a valid email"),
    dateOfBirth: yup.string(),
    contact: yup.string().required("Contact is required").matches(/^\+947\d{8}$/, 'Contact number follow format +947XXXXXXXX (e.g. +94771399297)').length(12),
    address: yup.string(),
});

export type FormValues = yup.InferType<typeof validationSchema>;

const BusinessUserForm: React.FC<BusinessUserFormProps> = ({ user, onClose }) => {
    const dispatch = useAppDispatch();
    const loggedInUser = useAppSelector(selectLoggedInUser);
    const permissions = getPermissionsForLoggedInUser(loggedInUser);
    const [roles] = useRoles();
    const { register, handleSubmit, formState: { errors } } = useForm<FormValues>({
        resolver: yupResolver(validationSchema),
        defaultValues: {
            username: user?.user.username ?? '',
            firstName: user?.firstName ?? '',
            lastName: user?.lastName ?? '',
            email: user?.email ?? '',
            roleId: user?.role?.id,
            dateOfBirth: user?.dateOfBirth ?? '',
            contact: user?.contact,
            address: user?.address ?? ''
        }
    });

    const onSubmitHandler = (data: any) => {
        // const selectedRoles = [roles.find(role => role.name === data.roles)];
        // data = { ...data, roles: selectedRoles };

        const updatedUser = {
            ...user,
            ...data
        }

        if(loggedInUser && !user?.id) {
            dispatch(createUser({ ...updatedUser, id: user?.id })).then(resp => {
                if (resp.meta.requestStatus === 'fulfilled') {
                    onClose();
                }
            });
            return;
        }

        loggedInUser && dispatch(updateBusinessUserProfile({ ...updatedUser, id: user?.id })).then(resp => {
            if (resp.meta.requestStatus === 'fulfilled') {
                onClose();
            }
        });
    };

    // const getRegister = (name: keyof FormValues, validation?: any): UseFormRegisterReturn => {
    //     return register(name, validation);
    // };

    return (
        // <Dialog open={true} onClose={onClose} sx={style} fullWidth maxWidth='md'>
        //     <DialogTitle sx={{ paddingBottom: 0 }}>User Details</DialogTitle>

            // <Box sx={style}>
            //     <>
            //         <FormCloseButton onClose={onClose} />
                    <form onSubmit={handleSubmit(onSubmitHandler)}>
                        <Grid container direction="row" className="mt-0 my-0" justifyContent='space-between'>
                            <FormField>
                                <FormTextField
                                    id="username"
                                    label="Username"
                                    name="username"
                                    defaultValue={user?.user.username}
                                    disabled={user !== undefined}
                                    errorMessage={errors.username?.message}
                                    register={register}
                                />
                            </FormField>
                            {(isAdmin(loggedInUser?.user) || permissions.includes(PermissionCode.ROLES_VIEW)) &&
                                <FormField>
                                    <FormSelectField
                                        id={"role"}
                                        label="Role"
                                        name="roleId"
                                        disabled={user?.id === loggedInUser?.roleData?.businessUser?.id}
                                        defaultValue={user?.role?.id}
                                        errorMessage={errors.roleId?.message}
                                        options={roles.map(role => { return { label: role.displayName, value: role.id } })}
                                        register={register}
                                    />
                                </FormField>
                            }
                            <FormField>
                                <FormTextField
                                    id="firstName"
                                    label="First Name"
                                    name="firstName"
                                    defaultValue={user?.firstName}
                                    errorMessage={errors.firstName?.message}
                                    register={register}
                                />
                            </FormField>
                            <FormField>
                                <FormTextField
                                    id="lastName"
                                    label="Last Name"
                                    name="lastName"
                                    defaultValue={user?.lastName}
                                    errorMessage={errors.lastName?.message}
                                    register={register}
                                />
                            </FormField>
                            <FormField>
                                <FormTextField
                                    id="email"
                                    label="Email"
                                    name="email"
                                    defaultValue={user?.email}
                                    errorMessage={errors.email?.message}
                                    register={register}
                                />
                            </FormField>
                            {/* <FormField>
                                <DropdownInput
                                    id={"gender"}
                                    label="Gender"
                                    defaultValue={user?.gender}
                                    error={errors.gender !== undefined}
                                    helperText={errors.gender?.message}
                                    options={["Male", "Female"].map(item => { return { label: item, value: item } })}
                                    register={register("gender")}
                                />
                            </FormField> */}
                            <FormField>
                                <FormTextField
                                    id="dateOfBirth"
                                    label="Birth Day"
                                    name="dateOfBirth"
                                    type="date"
                                    defaultValue={user?.dateOfBirth ?? ''}
                                    errorMessage={errors.dateOfBirth?.message}
                                    register={register}
                                />
                            </FormField>
                            <FormField>
                                <FormTextField
                                    id="contact"
                                    label="Contact"
                                    name="contact"
                                    defaultValue={user?.contact ?? "+94"}
                                    errorMessage={errors.contact?.message}
                                    register={register}
                                />
                            </FormField>
                            <FormField>
                                <FormTextField
                                    id="address"
                                    label="Address"
                                    name="address"
                                    defaultValue={user?.address}
                                    errorMessage={errors.address?.message}
                                    register={register}
                                />
                            </FormField>
                        </Grid>
                        <Grid container direction="row" justifyContent='end' sx={{ mt: 1 }}>
                            <Button type="button" variant="contained" sx={{ mr: 1, bgcolor: `${theme.palette.secondary.main}` }} onClick={onClose}>Cancel</Button>
                            <Button type="submit" variant="contained">Submit</Button>
                        </Grid>
                    </form>
            //     </>
            // </Box>
        // </Dialog>
    );
}

export default BusinessUserForm;